import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Users, Calendar, FileText, UserPlus, Upload, ClipboardList, CalendarCheck,
  TrendingUp, ArrowUpRight, Activity, Sparkles, Clock, MapPin,
} from 'lucide-react'
import { dashboardAPI } from '../../services/api'
import { useToast, getErrorMessage } from '../../components/Toast'
import { useAuthStore } from '../../store/authStore'
import StatRing from '../../components/StatRing'
import EmployeeStatusModal, { EmployeeStatusFilter } from '../../components/EmployeeStatusModal'

export default function AdminDashboard() {
  const navigate = useNavigate()
  const { error: toastError } = useToast()
  const { user } = useAuthStore()

  const [summary, setSummary] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [employeeStats, setEmployeeStats] = useState<any>(null)
  const [employeeStatsLoading, setEmployeeStatsLoading] = useState(true)
  const [activeModal, setActiveModal] = useState<EmployeeStatusFilter | null>(null)

  const loadEmployeeStats = async () => {
    setEmployeeStatsLoading(true)
    try {
      const res = await dashboardAPI.getEmployeeStats()
      setEmployeeStats(res.data)
    } catch (err: any) {
      toastError(getErrorMessage(err, 'Failed to load employee statistics.'))
    } finally {
      setEmployeeStatsLoading(false)
    }
  }

  function getLocalDateString(): string {
    const d = new Date()
    const yyyy = d.getFullYear()
    const mm = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${yyyy}-${mm}-${dd}`
  }

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const today = getLocalDateString()
        const response = await dashboardAPI.getSummary({ from: today, to: today })
        const cards = response.data.cards || {}
        setSummary(cards)
        setIsLoading(false)
      } catch (err: any) {
        const msg = getErrorMessage(err, 'Failed to load dashboard data.')
        setError(msg)
        toastError(msg)
        setIsLoading(false)
      }
    }

    fetchDashboardData()
    loadEmployeeStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const modalTitles: Record<EmployeeStatusFilter, string> = {
    at_work: 'Employees At Work Today',
    on_leave: 'Employees On Leave Today',
    absent: 'Absent Employees Today',
    checked_in_outside: 'Checked In Outside Geofence Today',
    checked_out_outside: 'Checked Out Outside Geofence Today',
  }

  const totalHeadcount = employeeStats?.total_headcount ?? 0
  const atWork = employeeStats?.at_work ?? 0
  const onLeave = employeeStats?.on_leave ?? 0
  const absent = employeeStats?.absent ?? 0
  const checkedInOutside = employeeStats?.check_in_outside_geofence ?? 0
  const checkedOutOutside = employeeStats?.check_out_outside_geofence ?? 0
  const checkedOutTotal = employeeStats?.checked_out ?? 0
  const presentRate = totalHeadcount > 0 ? Math.round((atWork / totalHeadcount) * 100) : 0

  const handleOpenModal = useCallback((status: EmployeeStatusFilter) => {
    requestAnimationFrame(() => setActiveModal(status))
  }, [])

  const handleCloseModal = useCallback(() => setActiveModal(null), [])

  const handleNavigate = useCallback((path: string) => {
    requestAnimationFrame(() => navigate(path))
  }, [navigate])

  const today = new Date().toLocaleDateString('en-IN', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  })

  // ── Time-based greeting ──
  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return 'Good Morning'
    if (hour < 17) return 'Good Afternoon'
    if (hour < 21) return 'Good Evening'
    return 'Good Night'
  }

  const roleLabel = user?.role === 'viewer' ? 'Viewer' : 'Admin'
  const greeting = getGreeting()

  return (
    <div className="p-6 md:p-8 bg-[#FAF6EE] min-h-full">
      {/* ── Header ── */}
      <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 bg-[#E4670F]/10 text-[#C24A0A] border border-[#E4670F]/20 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase mb-2">
            <Sparkles className="w-3 h-3" />
            Overview
          </div>
          <h1
            className="text-3xl font-bold text-neutral-900 mb-1"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {greeting}, {roleLabel}
          </h1>
          <p className="text-sm text-neutral-500">
            Here's how your workforce looks today across IRCS Haryana
          </p>
        </div>

        <div className="flex items-center gap-2 bg-white border border-neutral-200 rounded-xl px-3.5 py-2 shadow-sm">
          <Clock className="w-4 h-4 text-[#C24A0A]" />
          <span className="text-xs font-medium text-neutral-600">{today}</span>
        </div>
      </div>

      {error && (
        <div className="mb-6 flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 rounded-full border-2 border-neutral-200 border-t-[#E4670F] animate-spin" />
            <p className="text-sm text-neutral-500">Loading dashboard...</p>
          </div>
        </div>
      ) : (
        <>
          {/* ── Top KPI row ── */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
            {/* 1. Total Employees */}
            <div className="group relative bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 p-5 hover:shadow-lg hover:shadow-blue-500/5 hover:ring-blue-400/40 transition-all duration-300 overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-blue-500/[0.04] rounded-full blur-2xl group-hover:bg-blue-500/[0.08] transition-colors" />
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 text-white shadow-md shadow-blue-500/30 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                    <Users className="w-5 h-5" />
                  </div>
                  <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-blue-600 bg-blue-50 rounded-full px-2 py-0.5">
                    <Activity className="w-3 h-3" /> Live
                  </span>
                </div>
                <p className="text-sm font-medium text-neutral-500 mb-1">Total Employees</p>
                <p className="text-3xl font-bold text-neutral-900 leading-none">
                  {employeeStatsLoading ? '—' : totalHeadcount}
                </p>
                <p className="text-xs text-neutral-400 mt-1.5">Active headcount</p>
              </div>
            </div>

            {/* 2. Today's Attendance */}
            <div className="group relative bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 p-5 hover:shadow-lg hover:shadow-emerald-500/5 hover:ring-emerald-400/40 transition-all duration-300 overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/[0.04] rounded-full blur-2xl group-hover:bg-emerald-500/[0.08] transition-colors" />
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-md shadow-emerald-500/30 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                    <CalendarCheck className="w-5 h-5" />
                  </div>
                  <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold text-emerald-600 bg-emerald-50 rounded-full px-2 py-0.5">
                    <TrendingUp className="w-3 h-3" /> {presentRate}%
                  </span>
                </div>
                <p className="text-sm font-medium text-neutral-500 mb-3">Today's Attendance</p>
                <div className="space-y-1">
                  <button
                    onClick={() => handleOpenModal('at_work')}
                    className="w-full flex items-center justify-between px-2 py-1.5 -mx-2 rounded-lg hover:bg-emerald-50/70 transition-colors text-left group/row"
                  >
                    <span className="flex items-center gap-2 text-sm text-neutral-700">
                      <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-sm shadow-emerald-500/50" />
                      Present
                    </span>
                    <span className="font-semibold text-neutral-900 tabular-nums">
                      {employeeStatsLoading ? '—' : atWork}
                    </span>
                  </button>
                  <button
                    onClick={() => handleOpenModal('on_leave')}
                    className="w-full flex items-center justify-between px-2 py-1.5 -mx-2 rounded-lg hover:bg-amber-50/70 transition-colors text-left group/row"
                  >
                    <span className="flex items-center gap-2 text-sm text-neutral-700">
                      <span className="w-2 h-2 rounded-full bg-amber-500 shadow-sm shadow-amber-500/50" />
                      Leave
                    </span>
                    <span className="font-semibold text-neutral-900 tabular-nums">
                      {employeeStatsLoading ? '—' : onLeave}
                    </span>
                  </button>
                  <button
                    onClick={() => handleOpenModal('absent')}
                    className="w-full flex items-center justify-between px-2 py-1.5 -mx-2 rounded-lg hover:bg-red-50/70 transition-colors text-left group/row"
                  >
                    <span className="flex items-center gap-2 text-sm text-neutral-700">
                      <span className="w-2 h-2 rounded-full bg-red-500 shadow-sm shadow-red-500/50" />
                      Absent
                    </span>
                    <span className="font-semibold text-neutral-900 tabular-nums">
                      {employeeStatsLoading ? '—' : absent}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* 3. Pending Leave Requests */}
            <div className="group relative bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 p-5 hover:shadow-lg hover:shadow-orange-500/5 hover:ring-orange-400/40 transition-all duration-300 overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-orange-500/[0.04] rounded-full blur-2xl group-hover:bg-orange-500/[0.08] transition-colors" />
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-[#F0821F] to-[#C24A0A] text-white shadow-md shadow-[#E4670F]/30 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                    <FileText className="w-5 h-5" />
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-neutral-300 group-hover:text-[#C24A0A] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
                <p className="text-sm font-medium text-neutral-500 mb-1">Pending Leave Requests</p>
                <p className="text-3xl font-bold text-neutral-900 leading-none">
                  {summary?.pending_leave_requests ?? 0}
                </p>
                <p className="text-xs text-neutral-400 mt-1.5">Needs approval</p>
              </div>
            </div>

            {/* 4. Reports Submitted */}
            <div className="group relative bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 p-5 hover:shadow-lg hover:shadow-violet-500/5 hover:ring-violet-400/40 transition-all duration-300 overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-violet-500/[0.04] rounded-full blur-2xl group-hover:bg-violet-500/[0.08] transition-colors" />
              <div className="relative">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-500 to-violet-800 text-white shadow-md shadow-violet-500/30 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                    <FileText className="w-5 h-5" />
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-neutral-300 group-hover:text-violet-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
                <p className="text-sm font-medium text-neutral-500 mb-1">Reports Submitted</p>
                <p className="text-3xl font-bold text-neutral-900 leading-none">
                  {summary?.total_reports_submitted ?? 0}
                </p>
                <p className="text-xs text-neutral-400 mt-1.5">Today's reports</p>
              </div>
            </div>
          </div>

          {/* ── Second row ── */}
          <div className="grid grid-cols-1 lg:grid-cols-10 gap-5">
            {/* Employee Statistics */}
            <div className="lg:col-span-7 bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 p-7">
              <div className="flex items-center justify-between mb-1">
                <div>
                  <h2
                    className="text-lg font-bold text-neutral-900"
                    style={{ fontFamily: 'Georgia, serif' }}
                  >
                    Employee Statistics
                  </h2>
                  <p className="text-sm text-neutral-500 mt-1">
                    Headcount:{' '}
                    <span className="font-semibold text-neutral-800">
                      {employeeStatsLoading ? '—' : totalHeadcount}
                    </span>
                  </p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-md shadow-blue-500/30">
                  <Users className="w-5 h-5 text-white" />
                </div>
              </div>

              {employeeStatsLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 rounded-full border-2 border-neutral-200 border-t-[#E4670F] animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                  <StatRing
                    label="At Work"
                    value={atWork}
                    total={totalHeadcount}
                    color="#2563eb"
                    onClick={() => handleOpenModal('at_work')}
                  />
                  <StatRing
                    label="On Leave"
                    value={onLeave}
                    total={totalHeadcount}
                    color="#f59e0b"
                    onClick={() => handleOpenModal('on_leave')}
                  />
                  <StatRing
                    label="Absent"
                    value={absent}
                    total={totalHeadcount}
                    color="#dc2626"
                    onClick={() => handleOpenModal('absent')}
                  />
                </div>
              )}

              {/* Geofence — today's punches made outside the training centre radius */}
              {!employeeStatsLoading && (
                <div className="mt-6 pt-5 border-t border-neutral-100">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-orange-600" />
                    <h3 className="text-sm font-semibold text-neutral-800">Outside Geofence Today</h3>
                    <span className="text-xs text-neutral-400">click to see who</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <StatRing
                      label="Checked In Outside"
                      value={checkedInOutside}
                      total={atWork}
                      color="#ea580c"
                      onClick={() => handleOpenModal('checked_in_outside')}
                    />
                    <StatRing
                      label="Checked Out Outside"
                      value={checkedOutOutside}
                      total={checkedOutTotal}
                      color="#c2410c"
                      onClick={() => handleOpenModal('checked_out_outside')}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="lg:col-span-3 bg-white rounded-2xl shadow-sm ring-1 ring-neutral-200/70 p-6">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#F0821F] to-[#A31621] flex items-center justify-center shadow-md shadow-[#E4670F]/30">
                  <Sparkles className="w-4.5 h-4.5 text-white" />
                </div>
                <h2
                  className="text-lg font-bold text-neutral-900"
                  style={{ fontFamily: 'Georgia, serif' }}
                >
                  Quick Actions
                </h2>
              </div>

              <div className="space-y-2.5">
                <button
                  onClick={() => handleNavigate('/admin/employees')}
                  className="group w-full flex items-center gap-2.5 px-4 py-2.5 bg-gradient-to-r from-[#A31621] to-[#7A1F1F] hover:from-[#8A1219] hover:to-[#5F1818] text-white rounded-xl font-medium transition-all duration-300 shadow-md shadow-[#A31621]/20 hover:shadow-lg hover:shadow-[#A31621]/30 hover:-translate-y-0.5"
                >
                  <UserPlus className="w-4 h-4 transition-transform group-hover:scale-110" />
                  Add Employee
                </button>
                <button
                  onClick={() => handleNavigate('/admin/employees')}
                  className="group w-full flex items-center gap-2.5 px-4 py-2.5 border border-[#E4670F]/30 bg-[#E4670F]/[0.04] text-[#C24A0A] hover:bg-[#E4670F]/10 rounded-xl font-medium transition-all duration-300 hover:-translate-y-0.5"
                >
                  <Upload className="w-4 h-4 transition-transform group-hover:scale-110" />
                  Upload Employees
                </button>
                <button
                  onClick={() => handleNavigate('/admin/reports')}
                  className="group w-full flex items-center gap-2.5 px-4 py-2.5 border border-neutral-200 text-neutral-700 hover:bg-neutral-50 hover:border-neutral-300 rounded-xl font-medium transition-all duration-300 hover:-translate-y-0.5"
                >
                  <ClipboardList className="w-4 h-4 transition-transform group-hover:scale-110" />
                  View Reports
                </button>
                <button
                  onClick={() => handleNavigate('/admin/leave')}
                  className="group w-full flex items-center gap-2.5 px-4 py-2.5 border border-neutral-200 text-neutral-700 hover:bg-neutral-50 hover:border-neutral-300 rounded-xl font-medium transition-all duration-300 hover:-translate-y-0.5"
                >
                  <CalendarCheck className="w-4 h-4 transition-transform group-hover:scale-110" />
                  Review Leave Requests
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modal */}
      {activeModal && (
        <EmployeeStatusModal
          key={activeModal}
          status={activeModal}
          title={modalTitles[activeModal]}
          onClose={handleCloseModal}
        />
      )}
    </div>
  )
}